/**
 * Luật thuần của token AI cấp thêm, yêu cầu cấp thêm và túi token (ADR-0091 mục 2–4). Không dựng chuỗi hiển thị: trả
 * mã lỗi, view tự dịch. Ngày là chuỗi `YYYY-MM-DD` theo giờ Việt Nam, so sánh được bằng chuỗi.
 *
 * Kiểm ở đây chỉ để báo sớm cạnh ô nhập; tenant-service kiểm lại và là nơi quyết.
 */
import type {
  AiTokenDurationUnit,
  AiTokenGrant,
  AiTokenGrantKind,
  AiTokenPriority,
  AiTokenQuota,
  AiTokenQuotaBucket,
  AiTokenRequest,
  AiTokenRequestKind,
} from '../types.ts';
import { docSoToken } from './han-muc-token.ts';

/** Mỗi tenant tối đa 3 yêu cầu đang chờ (ADR-0091 mục 3). */
export const YEU_CAU_CHO_TOI_DA = 3;

/** Độ dài tối đa của lý do, ghi chú (`maxLength: 1000`). */
export const DO_DAI_GHI_CHU = 1000;

/** Thời hạn chọn được: nạp thêm 1, 3, 6, 12 tháng; gói tạm thời 1–4 tuần hoặc 1–12 tháng. */
export const THANG_NAP_THEM = [1, 3, 6, 12] as const;
export const TUAN_TAM_THOI = [1, 2, 3, 4] as const;
export const THANG_TAM_THOI = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

/** Túi cấp thêm "sắp hết hạn": `valid_to` trong 7 ngày tới (như `expiring_soon` của server). */
export const NGAY_SAP_HET_HAN = 7;

const hai = (n: number) => String(n).padStart(2, '0');
const ngay = (d: Date) => `${d.getUTCFullYear()}-${hai(d.getUTCMonth() + 1)}-${hai(d.getUTCDate())}`;

/** Hôm nay theo giờ Việt Nam (UTC+7, không có giờ mùa hè). */
export function homNayVN(bayGio: number = Date.now()): string {
  return ngay(new Date(bayGio + 7 * 3600_000));
}

/** Cộng `n` ngày. */
export function congNgay(d: string, n: number): string {
  const t = new Date(`${d}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + n);
  return ngay(t);
}

/** Cộng `n` tháng; ngày vượt tháng đích thì lấy ngày cuối tháng (như `LocalDate.plusMonths`). */
export function congThang(d: string, n: number): string {
  const [y, m, dd] = d.split('-').map(Number);
  const cuoi = new Date(Date.UTC(y, m - 1 + n + 1, 0)).getUTCDate();
  return ngay(new Date(Date.UTC(y, m - 1 + n, Math.min(dd, cuoi))));
}

/**
 * Ngày kết thúc từ thời hạn: `từ + N đơn vị − 1 ngày`, tính cả hai đầu — giống tenant-service. 1 tháng từ 26/10 là
 * tới 25/11; 1 tuần từ 26/10 là tới 01/11.
 */
export function ngayKetThuc(tu: string, donVi: AiTokenDurationUnit, soLuong: number): string {
  return congNgay(donVi === 'WEEK' ? congNgay(tu, 7 * soLuong) : congThang(tu, soLuong), -1);
}

/** Thời hạn hợp lệ theo loại: nạp thêm chỉ tháng 1/3/6/12; gói tạm thời tuần 1–4 hoặc tháng 1–12. */
export function thoiHanHopLe(loai: AiTokenGrantKind, donVi: AiTokenDurationUnit, soLuong: number): boolean {
  if (loai === 'NAP_THEM') return donVi === 'MONTH' && (THANG_NAP_THEM as readonly number[]).includes(soLuong);
  return donVi === 'WEEK'
    ? (TUAN_TAM_THOI as readonly number[]).includes(soLuong)
    : (THANG_TAM_THOI as readonly number[]).includes(soLuong);
}

/** Gia hạn được: chỉ gói tạm thời còn hiệu lực hoặc chưa tới ngày. */
export function giaHanDuoc(g: Pick<AiTokenGrant, 'kind' | 'status'>): boolean {
  return g.kind === 'GOI_TAM_THOI' && (g.status === 'ACTIVE' || g.status === 'SCHEDULED');
}

/** Thu hồi được: chưa thu hồi và chưa hết hạn — thu hồi dòng đã hết hạn không đổi gì. */
export function thuHoiDuoc(g: Pick<AiTokenGrant, 'status'>): boolean {
  return g.status === 'ACTIVE' || g.status === 'SCHEDULED';
}

/** Gói tạm thời Tenant Admin chọn được để xin gia hạn. */
export function goiTamThoiGiaHanDuoc(grants: readonly AiTokenGrant[]): AiTokenGrant[] {
  return grants.filter(giaHanDuoc);
}

/** Đã đủ số yêu cầu đang chờ: nút gửi vẫn hiện, hộp nói lý do (server cũng từ chối). */
export function daDuYeuCauCho(requests: readonly Pick<AiTokenRequest, 'status'>[]): boolean {
  return requests.filter((r) => r.status === 'PENDING').length >= YEU_CAU_CHO_TOI_DA;
}

/** Phần trăm đã dùng của một túi, kẹp 0–100, cho thanh nhỏ ở bảng và danh sách túi. */
export function phanTramTui(daDung: number, tong: number): number {
  if (tong <= 0) return 100;
  return Math.min(100, Math.max(0, Math.floor((daDung * 100) / tong)));
}

// ───────────────────────── túi token và thứ tự trừ ─────────────────────────

/** Một túi trong danh sách hiển thị: túi gói tài nguyên của tháng hoặc một túi cấp thêm. */
export type TuiToken =
  | { loai: 'goi'; tong: number; daDung: number; conLai: number; veKhong: string }
  | { loai: 'cap-them'; tui: AiTokenQuotaBucket };

/**
 * Các túi theo **thứ tự trừ** của `priority` (ADR-0091 mục 4): `HET_HAN_TRUOC` trừ túi gói tài nguyên trước (nó hết
 * hạn sớm nhất: cuối tháng), rồi túi cấp thêm theo `valid_to`; `MUA_THEM_TRUOC` trừ túi cấp thêm trước. Không giới hạn
 * (`base_limit` null) thì không có túi gói.
 */
export function cacTuiTheoThuTu(
  q: Pick<AiTokenQuota, 'base_limit' | 'base_used' | 'base_remaining' | 'grants' | 'priority' | 'resets_at'>,
): TuiToken[] {
  const capThem: TuiToken[] = [...q.grants]
    .sort((a, b) => a.valid_to.localeCompare(b.valid_to))
    .map((tui) => ({ loai: 'cap-them', tui }));
  if (q.base_limit === null) return capThem;
  const goi: TuiToken = {
    loai: 'goi',
    tong: q.base_limit,
    daDung: q.base_used,
    conLai: q.base_remaining ?? Math.max(0, q.base_limit - q.base_used),
    veKhong: q.resets_at,
  };
  return q.priority === 'MUA_THEM_TRUOC' ? [...capThem, goi] : [goi, ...capThem];
}

/** Tổng còn lại của các túi cấp thêm. */
export function conLaiCapThem(q: Pick<AiTokenQuota, 'grants'>): number {
  return q.grants.reduce((tong, g) => tong + g.remaining, 0);
}

/** Túi cấp thêm hết hạn sớm nhất, hoặc `undefined`. */
export function tuiHetHanSomNhat(q: Pick<AiTokenQuota, 'grants'>): AiTokenQuotaBucket | undefined {
  return [...q.grants].sort((a, b) => a.valid_to.localeCompare(b.valid_to))[0];
}

/**
 * Cảnh báo của `MUA_THEM_TRUOC`: token gói tài nguyên không dùng hết trong tháng mất vào cuối tháng. Chỉ đáng nói khi
 * có túi gói tài nguyên; trả số token đang có nguy cơ mất (`base_remaining`), 0 khi không cảnh báo.
 */
export function tokenSeMatCuoiThang(
  priority: AiTokenPriority,
  q: Pick<AiTokenQuota, 'base_limit' | 'base_remaining'>,
): number {
  if (priority !== 'MUA_THEM_TRUOC' || q.base_limit === null) return 0;
  return q.base_remaining ?? 0;
}

/** Đổi sang `MUA_THEM_TRUOC` cần hỏi lại (người dùng có thể mất token gói); đổi về mặc định thì không. */
export function canXacNhanDoiUuTien(cu: AiTokenPriority, moi: AiTokenPriority): boolean {
  return cu !== moi && moi === 'MUA_THEM_TRUOC';
}

/**
 * Hạn mức đặt riêng mới (hoặc bỏ đặt riêng, về `moi` là token của gói) làm tenant bị chặn ngay: túi gói đã dùng
 * chạm số mới và không còn túi cấp thêm nào có số dư.
 */
export function seChanVoiHanMucMoi(
  q: Pick<AiTokenQuota, 'base_used' | 'grants'>,
  moi: number | null,
): boolean {
  return moi !== null && q.base_used >= moi && conLaiCapThem(q) === 0;
}

// ───────────────────────── kiểm form ─────────────────────────

export type LoiSo = 'trong' | 'sai' | 'qua-lon' | 'bang-khong';
export type LoiNgay = 'trong' | 'truoc-hom-nay' | 'truoc-ngay-bat-dau' | 'khong-sau-hien-tai';

/** Đọc ô số token; 0 không hợp lệ ở đây (cấp thêm ít nhất 1 token). */
export function docTokenCapThem(nhap: string): { ok: true; so: number } | { ok: false; loi: LoiSo } {
  const kq = docSoToken(nhap);
  if (!kq.ok) return kq;
  if (kq.so < 1) return { ok: false, loi: 'bang-khong' };
  return kq;
}

export interface FormCapThem {
  kind: AiTokenGrantKind;
  tokens: string;
  /** Rỗng là hôm nay. */
  validFrom: string;
  donVi: AiTokenDurationUnit;
  soLuong: number;
  /** Ngày kết thúc tường minh, thắng thời hạn; rỗng là tính từ thời hạn. */
  validTo: string;
}

export interface KetQuaKiemCapThem {
  loi: { tokens?: LoiSo; validFrom?: LoiNgay; validTo?: LoiNgay; thoiHan?: 'sai' };
  /** Ngày bắt đầu và kết thúc sẽ áp (để xem trước), có cả khi ô số token còn lỗi. */
  khoang: { tu: string; den: string };
  /** Có khi không lỗi: thân gửi đi. */
  than?: {
    kind: AiTokenGrantKind;
    tokens: number;
    valid_from: string;
    valid_to: string | null;
    duration_unit: AiTokenDurationUnit;
    duration_count: number;
  };
}

/**
 * Kiểm hộp "Nạp thêm token" / "Thêm gói tạm thời" của Platform Admin, cũng là phần số và thời hạn của hộp duyệt yêu
 * cầu. Thân gửi cả thời hạn lẫn `valid_to` (nếu chọn ngày tường minh) — server lấy `valid_to` khi có.
 */
export function kiemCapThem(f: FormCapThem, homNay: string): KetQuaKiemCapThem {
  const loi: KetQuaKiemCapThem['loi'] = {};
  const so = docTokenCapThem(f.tokens);
  if (!so.ok) loi.tokens = so.loi;
  const tu = f.validFrom || homNay;
  if (!thoiHanHopLe(f.kind, f.donVi, f.soLuong)) loi.thoiHan = 'sai';
  const den = f.validTo || ngayKetThuc(tu, f.donVi, f.soLuong);
  if (f.validTo) {
    if (f.validTo < homNay) loi.validTo = 'truoc-hom-nay';
    else if (f.validTo < tu) loi.validTo = 'truoc-ngay-bat-dau';
  }
  const khoang = { tu, den };
  if (Object.keys(loi).length > 0 || !so.ok) return { loi, khoang };
  return {
    loi,
    khoang,
    than: {
      kind: f.kind,
      tokens: so.so,
      valid_from: tu,
      valid_to: f.validTo || null,
      duration_unit: f.donVi,
      duration_count: f.soLuong,
    },
  };
}

/** Kiểm ngày kết thúc mới khi gia hạn gói tạm thời: sau ngày hiện tại và không trước hôm nay. */
export function kiemGiaHan(hienTai: string, moi: string, homNay: string): LoiNgay | undefined {
  if (!moi) return 'trong';
  if (moi <= hienTai) return 'khong-sau-hien-tai';
  if (moi < homNay) return 'truoc-hom-nay';
  return undefined;
}

export interface FormYeuCau {
  kind: AiTokenRequestKind;
  tokens: string;
  donVi: AiTokenDurationUnit;
  soLuong: number;
  /** GIA_HAN: ngày kết thúc mới. */
  validTo: string;
  /** GIA_HAN: id gói tạm thời cần gia hạn (bắt buộc, ADR-0091 mục 3). */
  grantId: string;
  reason: string;
}

export interface KetQuaKiemYeuCau {
  loi: {
    tokens?: LoiSo;
    thoiHan?: 'sai';
    validTo?: LoiNgay;
    grantId?: 'trong';
    reason?: 'trong' | 'qua-dai';
  };
  than?: {
    kind: AiTokenRequestKind;
    tokens: number | null;
    duration_unit: AiTokenDurationUnit | null;
    duration_count: number | null;
    valid_to: string | null;
    grant_id: string | null;
    reason: string;
  };
}

/**
 * Kiểm hộp "Gửi yêu cầu" của Tenant Admin. Mỗi loại chỉ gửi trường của nó, trường khác là `null`:
 * - `NAP_THEM`: số token, thời hạn 1/3/6/12 tháng (tính từ ngày duyệt);
 * - `GOI_TAM_THOI`: số token trọn gói, thời hạn 1–4 tuần hoặc 1–12 tháng;
 * - `GIA_HAN`: gói tạm thời cần gia hạn và ngày kết thúc mới, sau ngày kết thúc hiện tại của gói đó.
 */
export function kiemYeuCau(
  f: FormYeuCau,
  homNay: string,
  hanHienTaiCuaGoi?: (grantId: string) => string | undefined,
): KetQuaKiemYeuCau {
  const loi: KetQuaKiemYeuCau['loi'] = {};
  const lyDo = f.reason.trim();
  if (!lyDo) loi.reason = 'trong';
  else if (lyDo.length > DO_DAI_GHI_CHU) loi.reason = 'qua-dai';

  let tokens: number | null = null;
  if (f.kind !== 'GIA_HAN') {
    const so = docTokenCapThem(f.tokens);
    if (so.ok) tokens = so.so;
    else loi.tokens = so.loi;
    if (!thoiHanHopLe(f.kind === 'NAP_THEM' ? 'NAP_THEM' : 'GOI_TAM_THOI', f.donVi, f.soLuong)) loi.thoiHan = 'sai';
  } else {
    if (!f.grantId) loi.grantId = 'trong';
    const hienTai = f.grantId ? hanHienTaiCuaGoi?.(f.grantId) : undefined;
    const l = hienTai
      ? kiemGiaHan(hienTai, f.validTo, homNay)
      : !f.validTo
        ? 'trong'
        : f.validTo < homNay
          ? 'truoc-hom-nay'
          : undefined;
    if (l) loi.validTo = l;
  }

  if (Object.keys(loi).length > 0) return { loi };
  const giaHan = f.kind === 'GIA_HAN';
  return {
    loi,
    than: {
      kind: f.kind,
      tokens,
      duration_unit: giaHan ? null : f.donVi,
      duration_count: giaHan ? null : f.soLuong,
      valid_to: giaHan ? f.validTo : null,
      grant_id: giaHan ? f.grantId : null,
      reason: lyDo,
    },
  };
}
